import { Component, Input, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { GeneralService } from '../../../shared/services/general.service';
import { firstValueFrom } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { InputGeneral } from '../../../shared/inputs/input-general/input-general';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { CommonModule, DecimalPipe } from '@angular/common';
import { FinancialStatementService } from '../../../shared/services/quotation/financial-statement/financial-statement.service';
import type {
  Period,
  FinancialConcept,
  Header,
} from '../../../shared/models/financial-statement/financial-statement.model';
import { LoadGeneralComponent } from '../../../shared/components-generals/load-general/load-general.component';
import { StaticPageService } from '../../../shared/services/static-page.service';
import { ClienteSTP } from '../../../shared/models/static-model/bureau.model';

@Component({
  selector: 'app-financial-statement',
  templateUrl: './financial-statement.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    InputGeneral,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslateModule,
    DecimalPipe,
    LoadGeneralComponent,
    MatTabsModule,
  ],
})
export class FinancialStatementComponent implements OnInit {
  @Input() public quotationId: number = 0;

  public applicantName: string = '';
  public periods: Period[] = [];
  public agroupConcepts: {
    typeEf: string;
    rows: {
      conceptName: string;
      valueType: string;
      isBold: boolean;
      periodData: { [periodNumber: number]: FinancialConcept };
    }[];
  }[] = [];
  public clientId: number = 0;
  public lastPeriod: number = 0;

  public periodControls: FormGroup = new FormGroup({});
  public conceptControls: FormGroup = new FormGroup({});
  public loadService = false;
  public clienteSTP: ClienteSTP | null = null;
  public allConcepts: FinancialConcept[] = [];
  public selectedTabIndex: number = 0;

  constructor(
    private financialStatementService: FinancialStatementService,
    private staticPageService: StaticPageService,
    private generalService: GeneralService,
    private cdr: ChangeDetectorRef,
  ) {}

  public async ngOnInit(): Promise<void> {
    // 1. Esperamos a que los datos iniciales se carguen completamente
    await this.getSTPInitialData();

    // 2. Cargamos los periodos desde el servicio (API) usando firstValueFrom
    this.periods = [];

    // 3. Creamos los controles dinámicos
    this.periods.forEach((p) => this.createControlPeriod(p));

    // 4. Se ejecuta hasta que los periodos ya existan
    await this.getLastPeriod();

    // 5. Cargamos los conceptos y los agrupamos
    const flatConcepts: FinancialConcept[] = [];
    this.groupConcepts(flatConcepts);
  }

  public getSTPInitialData(): void {
    this.loadService = true;
    this.staticPageService.getSTPInitialData(this.quotationId ?? 0).subscribe((data) => {
      this.clienteSTP = data;
      this.applicantName =
        (this.clienteSTP?.nombre ?? '') +
        ' ' +
        (this.clienteSTP?.paterno ?? '') +
        ' ' +
        (this.clienteSTP?.materno ?? '');

      if (this.clienteSTP && this.clienteSTP.iD_CLIENTE !== 0) {
        this.clientId = this.clienteSTP.iD_CLIENTE;
        this.clienteSTP.iS_EXISTING;
      }

      this.loadService = false;
    });
  }

  public async getHeaderData(period: number): Promise<void> {
    const data = await firstValueFrom(
      this.financialStatementService.getHeaderData({
        QuoteId: this.quotationId,
        ClientId: this.clientId,
        periodNumber: period,
        type: '',
        dispatch: '',
        date: new Date(),
        selected: false,
      }),
    );

    if (data) {
      // Por si el API devuelve un solo objeto o un arreglo de objetos.
      const headers: Header[] = Array.isArray(data)
        ? (data as unknown as Header[])
        : [data as unknown as Header];

      headers.forEach((header) => {
        // Formateamos la fecha a YYYY-MM-DD para que el input type="date" la reconozca.
        let formattedDate = '';
        if (header.year > 0 && header.month > 0 && header.day > 0) {
          const month = header.month.toString().padStart(2, '0');
          const day = header.day.toString().padStart(2, '0');
          formattedDate = `${header.year}-${month}-${day}`;
        }

        this.periodControls.patchValue({
          [`${header.periodNumber}_type`]: header.type || '',
          [`${header.periodNumber}_dispatch`]: header.dispatch || '',
          [`${header.periodNumber}_date`]: formattedDate,
          [`${header.periodNumber}_bank`]: header.bank || '',
          [`${header.periodNumber}_accountNumber`]: header.accountNumber || '',
          [`${header.periodNumber}_month`]: header.periodMonth || '',
        });

        const currentPeriod = this.periods.find((p) => p.periodNumber === header.periodNumber);
        if (currentPeriod) {
          currentPeriod.type = header.type || '';
          currentPeriod.dispatch = header.dispatch || '';
          currentPeriod.date = formattedDate ? (formattedDate as unknown as Date) : undefined;
          currentPeriod.bank = header.bank || '';
          currentPeriod.accountNumber = header.accountNumber || '';
          currentPeriod.periodMonth = header.periodMonth || '';
          currentPeriod.selected = header.checked === 1;
        }
      });
    }
  }

  public async getBodyData(period: number, origin?: number): Promise<void> {
    const efOriginToUse = origin ? origin : this.selectedTabIndex + 1;

    // Tipamos la respuesta directamente como FinancialConcept[]
    const response = await firstValueFrom(
      this.financialStatementService.getBodyData({
        QuoteId: this.quotationId,
        ClientId: this.clientId,
        periodNumber: period,
        EfOrigin: efOriginToUse,
      }),
    );
    const data = Array.isArray(response) ? (response as FinancialConcept[]) : [];

    if (data && data.length > 0) {
      // 1. Mapeamos el JSON de respuesta a la interfaz FinancialConcept
      const mappedConcepts: FinancialConcept[] = data.map((item: FinancialConcept) => ({
        idEeff: item.idEeff ?? item.id ?? 0, // item.id en caso de que venga del servicio así
        EFType: item.EFType,
        concept: item.concept,
        value: item.value,
        percentaje: item.percentaje,
        period: item.period,
        valueType: item.valueType,
        isBold: item.isBold || false,
      }));

      // 2. Filtramos los conceptos previos de este periodo (evita duplicados si actualizas)
      this.allConcepts = this.allConcepts.filter((c) => c.period !== period);

      // 3. Agregamos los nuevos conceptos a la lista global
      this.allConcepts = [...this.allConcepts, ...mappedConcepts];

      // 4. Mandamos a agrupar y crear controles dinámicos
      this.groupConcepts(this.allConcepts);
    }
  }

  // --- MÉTODOS DE INICIALIZACIÓN ---

  private groupConcepts(concepts: FinancialConcept[]): void {
    this.agroupConcepts = [];

    concepts.forEach((concept) => {
      // 1. Creación de controles
      if (concept.valueType !== 'CALCULADO') {
        const controlName = concept.idEeff.toString();
        if (!this.conceptControls.contains(controlName)) {
          this.conceptControls.addControl(controlName, new FormControl(concept.value));
        } else {
          this.conceptControls.get(controlName)?.setValue(concept.value, { emitEvent: false });
        }
      }

      // 2. Agrupación por Categoría (Ej: ACTIVO CIRCULANTE)
      let grupo = this.agroupConcepts.find((g) => g.typeEf === concept.EFType);
      if (!grupo) {
        grupo = { typeEf: concept.EFType, rows: [] };
        this.agroupConcepts.push(grupo);
      }

      // 3. Agrupación por Nombre de Concepto (Ej: CAJA)
      let fila = grupo.rows.find((r) => r.conceptName === concept.concept);
      if (!fila) {
        fila = {
          conceptName: concept.concept,
          valueType: concept.valueType,
          isBold: concept.isBold || false,
          periodData: {},
        };
        grupo.rows.push(fila);
      }

      // 4. Asignamos la data específica a la columna de este periodo
      fila.periodData[concept.period] = concept;
    });
  }

  // --- MÉTODOS DE FORMULARIOS DINÁMICOS (Requeridos por la vista HTML) ---

  public setPeriods(): void {
    for (let i = 0; i < this.lastPeriod; i++) {
      const period: Period = {
        QuoteId: this.quotationId,
        ClientId: this.clientId,
        periodNumber: i + 1,
        type: '',
        dispatch: '',
        date: new Date(),
        selected: false,
      };
      this.periods.unshift(period);
      this.createControlPeriod(period);
      this.getHeaderData(i + 1);
      this.getBodyData(i + 1);
    }
  }

  public getControl(numer: number, field: string): FormControl {
    const key = `${numer}_${field}`;
    return this.periodControls.get(key) as FormControl;
  }

  public getControlValue(idEeff: number): FormControl {
    return this.conceptControls.get(idEeff.toString()) as FormControl;
  }

  public createControlPeriod(period: Period): void {
    this.periodControls.addControl(
      `${period.periodNumber}_type`,
      new FormControl(period.type || ''),
    );
    this.periodControls.addControl(
      `${period.periodNumber}_dispatch`,
      new FormControl(period.dispatch || ''),
    );
    this.periodControls.addControl(
      `${period.periodNumber}_date`,
      new FormControl(period.date || ''),
    );

    //CONTROLES (pestaña 5)
    this.periodControls.addControl(
      `${period.periodNumber}_bank`,
      new FormControl(period.bank || ''),
    );
    this.periodControls.addControl(
      `${period.periodNumber}_accountNumber`,
      new FormControl(period.accountNumber || ''),
    );
    this.periodControls.addControl(
      `${period.periodNumber}_month`,
      new FormControl(period.periodMonth || ''),
    );
  }

  public async getLastPeriod() {
    // 1. Convertimos el Observable en Promesa con firstValueFrom
    const response = (await firstValueFrom(
      this.financialStatementService.getLastPeriod({
        QuoteId: this.quotationId,
        ClientId: this.clientId,
        periodNumber: 0,
        type: '',
        dispatch: '',
        date: new Date(),
        selected: false,
      }),
    )) as Period;

    // 2. Asignamos el valor obtenido
    this.lastPeriod = response?.periodNumber ?? 0;

    this.setPeriods();
  }

  public async addPeriod(): Promise<void> {
    // 1. Mandamos crear el periodo y esperamos el resultado
    const response = (await firstValueFrom(
      this.financialStatementService.InsertFinancialStatement({
        QuoteId: this.quotationId,
        ClientId: this.clientId,
        periodNumber: this.lastPeriod + 1,
        type: '',
        dispatch: '',
        date: new Date(),
        selected: false,
      }),
    )) as Period;

    // 2. Actualizamos nuestro contador
    this.lastPeriod = response?.periodNumber ?? this.lastPeriod + 1;

    // 3. Creamos el objeto local
    const newPeriod: Period = {
      QuoteId: this.quotationId,
      periodNumber: this.lastPeriod,
      ClientId: this.clientId,
      type: '',
      dispatch: '',
      selected: false,
      // No enviamos `date: '' as any`, simplemente se omite porque es opcional en la interfaz Period.
    };

    // 4. Agregamos la nueva columna y sus controles
    this.periods.unshift(newPeriod);
    this.createControlPeriod(newPeriod);

    // 5. Descargamos los datos del nuevo periodo (esto actualizará la tabla sola)
    await this.getHeaderData(this.lastPeriod);
    await this.getBodyData(this.lastPeriod);
  }

  public async deletePeriod(numero: number): Promise<void> {
    await firstValueFrom(
      this.financialStatementService.deletePeriod({
        QuoteId: this.quotationId,
        ClientId: this.clientId,
        periodNumber: numero,
        type: '',
        dispatch: '',
        date: new Date(),
        selected: false,
      }),
    );

    // 1. Quitamos la columna de la lista de periodos
    this.periods = this.periods.filter((p) => p.periodNumber !== numero);

    // 2. Destruimos los controles del header
    this.periodControls.removeControl(`${numero}_type`);
    this.periodControls.removeControl(`${numero}_dispatch`);
    this.periodControls.removeControl(`${numero}_date`);

    // 3. Quitamos TODOS los conceptos que pertenecían a ese periodo del caché global
    this.allConcepts = this.allConcepts.filter((c) => c.period !== numero);

    // 4. Reconstruimos los grupos (esto borra la columna de la tabla visualmente)
    this.groupConcepts(this.allConcepts);

    await this.getHeaderData(this.lastPeriod);
    await this.getBodyData(this.lastPeriod);
  }

  public setPeriodo(numero: number, checked: boolean): void {
    const periodo = this.periods.find((p) => p.periodNumber === numero);
    if (periodo) {
      periodo.selected = checked;
    }
  }

  public updateHeaderData(updateType: number, periodNumber: number, isChecked?: boolean): void {
    // 1. Buscamos el periodo actual para actualizar su estado de selección si viene del checkbox
    const period = this.periods.find((p) => p.periodNumber === periodNumber);
    if (isChecked !== undefined && period) {
      period.selected = isChecked;
    }

    // 2. Construimos el payload usando tipado estricto
    const payload: Period = {
      QuoteId: this.quotationId,
      ClientId: this.clientId,
      periodNumber: periodNumber,
      type: this.getControl(periodNumber, 'type')?.value || '',
      dispatch: this.getControl(periodNumber, 'dispatch')?.value || '',
      date: this.getControl(periodNumber, 'date')?.value || new Date(),
      bank: this.getControl(periodNumber, 'bank')?.value || '',
      accountNumber: this.getControl(periodNumber, 'accountNumber')?.value || '',
      periodMonth: this.getControl(periodNumber, 'month')?.value || '',
      checked: period?.selected ? 1 : 0,
      updateType: updateType,
    };

    this.financialStatementService.updateHeaderData(payload).subscribe({
      next: () => {
        this.generalService.viewSnacAlert('Datos guardados', 'normal', 'Completado');
      },
      error: (err) => {
        console.error('Error en updateHeaderData:', err);
        this.generalService.viewSnacAlert('Error al guardar datos', 'error', 'Error');
      },
    });
  }

  public updateValue(idEeff: number): void {
    const control = this.getControlValue(idEeff);
    if (control.valid) {
      const newValue = control.value;
      const newConcept: FinancialConcept = {
        idEeff: idEeff,
        EFType: '',
        concept: '',
        value: newValue,
        period: 0,
        percentaje: 0,
        valueType: 'CAPTURADO',
      };

      this.financialStatementService.updateBodyData(newConcept).subscribe({
        next: () => {
          this.generalService.viewSnacAlert(
            'Valor actualizado correctamente',
            'normal',
            'Completado',
          );
        },
        error: (err) => {
          console.error('Error en updateValue:', err);
          this.generalService.viewSnacAlert('Error al actualizar el valor', 'error', 'Error');
        },
      });
    }
  }

  public async updateAllValues(): Promise<void> {
    const newPeriod: Period = {
      QuoteId: this.quotationId,
      periodNumber: this.lastPeriod,
      ClientId: this.clientId,
      type: '',
      dispatch: '',
      selected: false,
      // date no se manda aquí ya que es opcional.
    };
    this.financialStatementService.updateAllValues(newPeriod).subscribe({
      next: () => {
        this.generalService.viewSnacAlert(
          'Todos los periodos han sido actualizados',
          'normal',
          'Completado',
        );
      },
      error: (err) => {
        console.error('Error en updateAllValues:', err);
        this.generalService.viewSnacAlert('Error al actualizar los periodos', 'error', 'Error');
      },
    });

    await this.getHeaderData(this.lastPeriod);
    await this.getBodyData(this.lastPeriod);
  }

  public async onTabChange(newIndex: number): Promise<void> {
    this.selectedTabIndex = newIndex;
    this.loadService = true;
    this.allConcepts = [];
    this.agroupConcepts = [];
    this.conceptControls = new FormGroup({});

    try {
      if (newIndex !== 6) {
        // --- COMPORTAMIENTO NORMAL PARA LAS PESTAÑAS DEL 1 AL 5 ---
        const bodyPromises = this.periods.map((period) =>
          this.getHeaderData(period.periodNumber).then(() =>
            this.getBodyData(period.periodNumber, newIndex + 1),
          ),
        );
        await Promise.all(bodyPromises);
      } else {
        // --- COMPORTAMIENTO ESPECIAL PARA IMPRESIONES (Índice 6) ---
        const originsToLoad = [1, 2, 3, 4];

        const bodyPromises = this.periods.map(async (period) => {
          await this.getHeaderData(period.periodNumber);
          // Cargamos todos los orígenes secuencialmente para este periodo
          for (const origin of originsToLoad) {
            await this.getBodyData(period.periodNumber, origin);
          }
        });

        await Promise.all(bodyPromises);
      }
    } catch (error) {
      console.error('Error al cambiar de pestaña:', error);
    } finally {
      this.loadService = false;
      this.cdr.detectChanges();
    }
  }

  public get hasSelectedPeriods(): boolean {
    return this.periods.some((period) => period.selected);
  }

  public get printPeriodChunks(): Period[][] {
    const selectedPeriods = this.periods.filter((p) => p.selected);
    const chunkSize = 3;
    const chunks: Period[][] = [];

    for (let i = 0; i < selectedPeriods.length; i += chunkSize) {
      chunks.push(selectedPeriods.slice(i, i + chunkSize));
    }

    return chunks;
  }

  public printReport(): void {
    window.print();
  }
}
